/**
 * Налаштування — усе, що не має бути на сервері, лежить у localStorage.
 * Найважливіше: список Gemini API-ключів для ротації.
 *
 * Ключі НІКОЛИ не йдуть у D1 і не пишуться в лог сервера — тіло запиту з ключем
 * іде прямо на Pages Function, а той маскує його в логах (functions/_shared/gemini.js).
 */

const KEY = 'nmt.settings.v1'

export const DEFAULTS = {
  /** Список ключів Gemini — саме з нього Worker бере в ротацію. */
  geminiKeys: [],
  geminiModel: 'gemini-3.8-flash',

  /** 'fsrs' | 'sm2' */
  algorithm: 'fsrs',
  /** Бажана ймовірність пригадування: 0.7 … 0.97 (за замовчуванням 0.9). */
  desiredRetention: 0.9,
  /** Нових карток на день. */
  newPerDay: 20,
  /** Максимум повторень за день. */
  maxReviewsPerDay: 200,

  /** Озвучка. */
  speechEnabled: true,
  /** 'google' — новий рушій за замовчуванням, 'browser' — запасний системний. */
  ttsEngine: 'google',
  /** Швидкість і голос потрібні лише запасному системному рушію. */
  speechRate: 0.9,
  speechVoice: '',

  /** Скільки карток показувати в сесії. */
  sessionSize: 20,
  /** Показувати кнопку «Забув» (4 оцінки замість 3). */
  showAgainButton: false,
  /** Підсвічувати ударну букву на картках із тегом #наголоси. */
  highlightStress: true,
  /** Автозапуск озвучки при перевертанні картки. */
  autoSpeak: false,

  theme: 'dark',
}

function read() {
  if (typeof localStorage === 'undefined') return { ...DEFAULTS }
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { ...DEFAULTS }
    const parsed = JSON.parse(raw)
    const rest = { ...(parsed ?? {}) }
    // Невідомий рушій зі старого сховища не має ламати вибір: повертаємо Google.
    if (rest.ttsEngine !== 'browser' && rest.ttsEngine !== 'google') rest.ttsEngine = 'google'
    return { ...DEFAULTS, ...rest, geminiKeys: normalizeKeys(parsed?.geminiKeys) }
  } catch {
    return { ...DEFAULTS }
  }
}

let lastWriteError = null

function write(next) {
  if (typeof localStorage === 'undefined') return next
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
    lastWriteError = null
  } catch (err) {
    // localStorage може бути недоступним або переповненим. Не блокуємо UI,
    // але зберігаємо причину, щоб компонент показав зрозумілий статус.
    lastWriteError = err
  }
  return next
}

/** Остання помилка запису localStorage, якщо автозбереження не вдалося. */
export const getSettingsWriteError = () => lastWriteError

/** Приводить ключі до масиву рядків без порожніх і дублів. */
export function normalizeKeys(input) {
  if (!input) return []
  const list = Array.isArray(input) ? input : String(input).split(/[\s,;\n]+/)
  const out = []
  for (const raw of list) {
    const k = String(raw).trim()
    if (k && !out.includes(k)) out.push(k)
  }
  return out
}

/** Ключі у вигляді тексту для textarea (по одному на рядок). */
export const keysToText = (keys) => (keys ?? []).join('\n')

let cache = read()

export const getSettings = () => ({ ...cache })

/** Оновлює налаштування (п частковий об’єкт) і перезаписує localStorage. */
export function setSettings(patch) {
  const next = { ...cache, ...patch }
  if (patch.geminiKeys) next.geminiKeys = normalizeKeys(patch.geminiKeys)
  cache = next
  write(next)
  return { ...next }
}

export function resetSettings() {
  cache = { ...DEFAULTS }
  write(cache)
  return { ...cache }
}

/** Підписка для useSyncExternalStore. */
const listeners = new Set()
export function subscribeSettings(cb) {
  listeners.add(cb)
  const onStorage = (e) => {
    if (e.key === KEY) {
      cache = read()
      listeners.forEach((l) => l())
    }
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(cb)
    window.removeEventListener('storage', onStorage)
  }
}

export const getSettingsSnapshot = () => cache

/** Маска для відображення ключа в інтерфейсі. */
export function maskApiKey(key) {
  const k = String(key ?? '')
  if (k.length <= 12) return k ? `${k.slice(0, 3)}…` : ''
  return `${k.slice(0, 6)}…${k.slice(-4)}`
}

/** Експорт/імпорт усіх налаштувань (щоб перенести на інший комп’ютер). */
export const exportSettings = () => JSON.stringify(getSettings(), null, 2)

export function importSettings(json) {
  try {
    const parsed = JSON.parse(json)
    return setSettings(parsed)
  } catch {
    return getSettings()
  }
}
